import { Modal } from "react-bootstrap";
import find from "../../../services/search-assets/find";
import { useEffect, useState } from "react";
import { DebounceInput } from "react-debounce-input";
import { AssetDetails, eAssetType } from "../../../@types/Asset";
import useIcons from "../../../hooks/useIcons";
import { parseAssetTypeInt, search } from "../../../utils";
import Skeleton from "react-loading-skeleton";
import { RiEmotionSadLine } from "react-icons/ri";
import { GiRocket } from "react-icons/gi";
import { AiOutlineClose } from "react-icons/ai";
import useWallet from "../../../hooks/useWallet";
import Swal from "sweetalert2";
import { projectPrimaryColor } from "../../../generalConfigs";

type Props = {
  show: boolean;
  onClickClose: CallableFunction;
  callOnEnd: CallableFunction;
};
export default function AddAssetModal({
  show,
  onClickClose,
  callOnEnd,
}: Props) {
  const [searchValue, setSearchValue] = useState<string>("");
  const [assets, setAssets] = useState<AssetDetails[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [updateOnClose, setUpdateOnClose] = useState<boolean>(false);

  function handleOnClickClose() {
    if (updateOnClose) {
      return callOnEnd();
    }

    return onClickClose();
  }

  const { addAssetByEncodedId } = useWallet();

  const { getURI } = useIcons();

  useEffect(() => {
    async function fetchData() {
      setIsLoading(true);
      setIsError(false);

      if (searchValue.length > 0) {
        const response = await find(searchValue);

        if (response.requestStatus === 2) {
          setAssets(
            response.results
              .filter(
                (asset: any) =>
                  asset.type === eAssetType.CRIPTO ||
                  asset.type === eAssetType.STOCK ||
                  asset.type === eAssetType.FIIS
              )
              .map((asset: any) => {
                return {
                  ...asset,
                  encoded_id: asset.id,
                };
              })
          );
        } else if (response.requestStatus === 1) {
          setAssets([]);
        } else if (response.requestStatus === 0) {
          setIsError(true);
        }
      } else {
        setAssets(defaultAssets);
      }

      setIsLoading(false);
    }

    fetchData();
  }, [searchValue]);

  async function handleAddAsset(
    name: string,
    symbol: string,
    encodedId: string
  ) {
    Swal.fire({
      title: `Adicionar ${symbol} à sua carteira?`,
      text: `Tem certeza de que deseja adicionar ${name} (${symbol}) à sua carteira?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: projectPrimaryColor,
      cancelButtonColor: "gray",
      confirmButtonText: "Sim",
      cancelButtonText: "Cancelar",
      background: "var(--bg-1)",
      color: "var(--text-1)",
    }).then(async (result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: "Adicionando...",
          html: '<div style="overflow: hidden"><div class="spinner-border"></div></div>',
          background: "var(--bg-1)",
          color: "var(--text-1)",
          showConfirmButton: false,
          allowOutsideClick: false,
          allowEscapeKey: false,
        });

        const status = await addAssetByEncodedId(encodedId);

        if (status === 201) {
          setUpdateOnClose(true);

          Swal.fire({
            title: "Sucesso!",
            text: `${name} (${symbol}) foi adicionado à sua carteira.`,
            icon: "success",
            confirmButtonColor: projectPrimaryColor,
            background: "var(--bg-1)",
            color: "var(--text-1)",
          });
        } else if (status === 409) {
          Swal.fire({
            title: "Ops!",
            text: `${name} (${symbol}) já está na sua carteira.`,
            icon: "info",
            confirmButtonColor: projectPrimaryColor,
            background: "var(--bg-1)",
            color: "var(--text-1)",
          });
        } else if (status === 422) {
          Swal.fire({
            title: "Limite atingido.",
            text: `O máximo de ativos que seu plano atual permite adicionar na carteira foi atingido, você pode remover ativos ou atualizar para um plano com um limite maior (Clicando na sua foto de perfil).`,
            icon: "info",
            confirmButtonColor: projectPrimaryColor,
            background: "var(--bg-1)",
            color: "var(--text-1)",
          });
        } else {
          Swal.fire({
            title: "Ops!",
            text: "Infelizmente, aconteceu um erro desconhecido, por favor, tente novamente.",
            icon: "error",
            confirmButtonColor: projectPrimaryColor,
            background: "var(--bg-1)",
            color: "var(--text-1)",
          });
        }
      }
    });
  }

  function content() {
    if (isError) {
      return (
        <div>
          <div className="pos-relative mg-t-20">
            <div className="input-group-text" id="step-search-bar">
              <div className="d-flex pos-absolute t-8 l-25">
                <i className="icon ion-md-search" />
              </div>
              <DebounceInput
                value={searchValue}
                autoFocus={true}
                placeholder="Pesquise por ativos..."
                className="form-control pd-l-30"
                minLength={2}
                debounceTimeout={500}
                onChange={(e: any) => setSearchValue(e.target.value)}
              />
            </div>
          </div>
          <div className="text-center pd-20 mg-b-20">
            <span>
              <GiRocket size={48} />
            </span>
            <h4>Houston, temos um problema!</h4>
            <span>
              Infelizmente, os dados não aterrissaram da maneira correta.
              <br />
              Por favor, <b>tente novamente.</b>
            </span>{" "}
          </div>
        </div>
      );
    } else if (isLoading) {
      return <LoadingStateBody />;
    }

    return (
      <Modal.Body style={{ maxHeight: "90vh", overflowY: "auto" }}>
        <div className="pos-relative">
          <div className="input-group-text" id="step-search-bar">
            <div className="d-flex pos-absolute t-8 l-25">
              <i className="icon ion-md-search" />
            </div>
            <DebounceInput
              value={searchValue}
              autoFocus={true}
              placeholder="Pesquise por ativos..."
              className="form-control pd-l-30"
              minLength={2}
              debounceTimeout={500}
              onChange={(e: any) => setSearchValue(e.target.value)}
            />
          </div>
        </div>
        <div className="mg-t-20">
          <span className="az-notification-title">Sugestões</span>
          <p className="az-notification-text">Ativos mais adicionados</p>
          <div className="az-notification-list">
            {assets.length === 0 ? (
              <>
                <div className="text-center">
                  <span>
                    <RiEmotionSadLine size={48} />
                  </span>
                  <h4>Nenhum ativo encontrado, por enquanto.</h4>
                  <span>
                    Todos os dias adicionamos milhares de ativos novos a
                    plataforma, tente buscar novamente depois.
                  </span>
                </div>
              </>
            ) : (
              assets.map((asset) => {
                return (
                  <div
                    className="media"
                    onClick={() =>
                      handleAddAsset(asset.name, asset.symbol, asset.encoded_id)
                    }
                  >
                    <img
                      className="img-xs rounded-circle"
                      src={getURI(asset.icon)}
                    />
                    <div className="media-body">
                      <div>
                        <a>
                          <strong className="tx-primary"> {asset.name}</strong>{" "}
                        </a>
                      </div>
                      <span className="tx-12">
                        Ticker:{" "}
                        <strong className="tx-warning">{asset.symbol}</strong>
                      </span>
                      <span>
                        {parseAssetTypeInt(asset.type)} ({asset.exchange})
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </Modal.Body>
    );
  }

  return (
    <Modal show={show} onHide={() => handleOnClickClose()}>
      <Modal.Header>
        <Modal.Title>Adicionar ativos</Modal.Title>
        <AiOutlineClose
          onClick={() => handleOnClickClose()}
          size={23}
          className="float-right cursor-pointer"
        />
      </Modal.Header>

      {content()}
    </Modal>
  );
}

function LoadingStateBody() {
  return (
    <Modal.Body>
      <div className="pos-relative"></div>
      <div className="az-notification-list">
        {[0, 1, 2, 3, 4].map((item) => {
          return (
            <div className="media" key={item}>
              <Skeleton width={30} height={30} borderRadius={50} />
              <div className="media-body">
                <div>
                  <Skeleton className="mg-t-10" />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Modal.Body>
  );
}

const defaultAssets: AssetDetails[] = [
  {
    encoded_id: "ans7o",
    status: 1,
    name: "Prio SA",
    symbol: "PRIO3",
    exchange: "BM&FBovespa",
    type: 1,
    country_code: 32,
    icon: "PRIO3.svg",
    currency_symbol: "BRL",
  },
  {
    encoded_id: "se7o",
    status: 1,
    name: "Nvidia Corporation",
    symbol: "NVDA",
    exchange: "NASDAQ",
    type: 1,
    country_code: 5,
    icon: "NVDA.svg",
    currency_symbol: "USD",
  },

  {
    encoded_id: "afroh77",
    status: 1,
    name: "Xp Malls Fundo de Investimento Imobil...",
    symbol: "XPML11",
    exchange: "BM&FBovespa",
    type: 4,
    country_code: 32,
    icon: "XPML11.svg",
    currency_symbol: "BRL",
  },
  {
    encoded_id: "aftenfo",
    status: 1,
    name: "Bitcoin Real Brasileiro",
    symbol: "BTC/BRL",
    exchange: "MercadoBitcoin",
    type: 3,
    country_code: 241,
    icon: "BTCBRL.svg",
    currency_symbol: "BRL",
  },
  {
    encoded_id: "aarrnst",
    status: 1,
    name: "Btg Pactual Logística Fundo de Invest...",
    symbol: "BTLG11",
    exchange: "BM&FBovespa",
    type: 4,
    country_code: 32,
    icon: "BTLG11.svg",
    currency_symbol: "BRL",
  },
  {
    encoded_id: "77osrf",
    status: 1,
    name: "Ethereum Dólar Americano",
    symbol: "ETH/USD",
    exchange: "Bitfinex",
    type: 3,
    country_code: 249,
    icon: "ETHUSD.svg",
    currency_symbol: "USD",
  },
];
